import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Shield, Download, AlertTriangle, CheckCircle2, Clock, XCircle,
  Activity, FileText, BarChart3, TrendingUp, Filter, ChevronDown,
  Search, Printer, RefreshCw, Eye, ArrowUpRight
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend
} from "recharts";

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
};

const SEVERITY_COLORS = { CRITICAL: "#991B1B", HIGH: "#EA580C", MEDIUM: "#D97706", LOW: "#2563EB", INFO: "#94A3B8" };
const STATUS_COLORS = { open: "#EF4444", in_progress: "#F59E0B", resolved: "#10B981" };
const PIE_COLORS = ["#EF4444", "#EA580C", "#D97706", "#2563EB", "#94A3B8"];

const SeverityBadge = ({ severity }) => {
  const styles = {
    CRITICAL: "bg-red-100 text-red-800 border-red-200",
    HIGH: "bg-orange-100 text-orange-800 border-orange-200",
    MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
    LOW: "bg-blue-100 text-blue-800 border-blue-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[severity] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {severity}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color = "#FF7A59" }) => (
  <Card className="bg-white border border-slate-200/80 hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const TestResultsCenterPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [frameworkFilter, setFrameworkFilter] = useState("all");
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/analytics/test-results-center");
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load test results");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportCSV = async () => {
    try {
      const res = await api.get("/compliance-dashboard/findings/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `test_results_gaps_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const handleExportPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get("/analytics/test-results-center/pdf-data");
      const pdf = res.data;
      generatePDF(pdf);
      toast.success("PDF scorecard generated");
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const generatePDF = (pdf) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow popups"); return; }

    const frameworkRows = pdf.frameworks.map(f =>
      `<tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;font-weight:600">${f.name}</td>
       <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center"><span style="font-size:20px;font-weight:800;color:${f.score >= 80 ? '#0E8A55' : f.score >= 60 ? '#D97706' : '#C7322A'}">${f.score}</span>/100</td>
       <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center;color:#0E8A55">${f.pass}</td>
       <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center;color:#EF4444">${f.fail}</td>
       <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center;color:#D97706">${f.warn}</td>
       <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center">${f.total}</td></tr>`
    ).join("");

    const findingsRows = pdf.critical_findings.slice(0, 15).map(f =>
      `<tr><td style="padding:6px;border-bottom:1px solid #e2e8f0;font-size:11px"><span style="background:${f.severity === 'CRITICAL' ? '#FEE2E2' : '#FFEDD5'};padding:2px 6px;border-radius:4px;font-weight:600">${f.severity}</span></td>
       <td style="padding:6px;border-bottom:1px solid #e2e8f0;font-size:11px">${f.framework}</td>
       <td style="padding:6px;border-bottom:1px solid #e2e8f0;font-size:11px;font-family:monospace">${f.control_id}</td>
       <td style="padding:6px;border-bottom:1px solid #e2e8f0;font-size:11px">${f.finding}</td></tr>`
    ).join("");

    w.document.write(`<!DOCTYPE html><html><head><title>Odesie Compliance Scorecard</title>
    <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:0;padding:40px;color:#1e293b}
    @media print{body{padding:20px}}</style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:30px;border-bottom:3px solid #FF7A59;padding-bottom:20px">
      <div><h1 style="margin:0;font-size:28px;color:#0f172a">${pdf.report_title}</h1>
      <p style="margin:4px 0 0;color:#64748b;font-size:13px">Generated: ${new Date(pdf.generated_at).toLocaleString()}</p></div>
      <div style="text-align:center;background:#0f172a;color:white;padding:16px 24px;border-radius:12px">
        <div style="font-size:36px;font-weight:800">${pdf.overall_score}</div>
        <div style="font-size:11px;color:#94a3b8">/100 Overall</div></div></div>
    <h2 style="font-size:18px;margin:24px 0 12px;color:#0f172a">Framework Scores</h2>
    <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
    <thead><tr style="background:#f1f5f9"><th style="padding:10px;text-align:left;font-size:12px;text-transform:uppercase;color:#64748b">Framework</th>
    <th style="padding:10px;text-align:center;font-size:12px;text-transform:uppercase;color:#64748b">Score</th>
    <th style="padding:10px;text-align:center;font-size:12px;text-transform:uppercase;color:#64748b">Pass</th>
    <th style="padding:10px;text-align:center;font-size:12px;text-transform:uppercase;color:#64748b">Fail</th>
    <th style="padding:10px;text-align:center;font-size:12px;text-transform:uppercase;color:#64748b">Warn</th>
    <th style="padding:10px;text-align:center;font-size:12px;text-transform:uppercase;color:#64748b">Total</th></tr></thead>
    <tbody>${frameworkRows}</tbody></table>
    ${pdf.critical_findings.length > 0 ? `<h2 style="font-size:18px;margin:24px 0 12px;color:#C7322A">Critical & High Findings (${pdf.critical_findings.length})</h2>
    <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0"><thead><tr style="background:#FEF2F2">
    <th style="padding:8px;text-align:left;font-size:11px">Severity</th>
    <th style="padding:8px;text-align:left;font-size:11px">Framework</th>
    <th style="padding:8px;text-align:left;font-size:11px">Control</th>
    <th style="padding:8px;text-align:left;font-size:11px">Finding</th></tr></thead>
    <tbody>${findingsRows}</tbody></table>` : ''}
    <div style="margin-top:30px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;color:#94a3b8;font-size:11px">
    Odesie LMS - Confidential Compliance Scorecard - ${new Date().toLocaleDateString()}</div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  const s = data?.summary || {};
  const allFindings = data?.all_findings || [];
  const filteredFindings = allFindings.filter(f => {
    if (severityFilter !== "all" && f.severity !== severityFilter) return false;
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (frameworkFilter !== "all" && f.framework.toLowerCase() !== frameworkFilter) return false;
    if (searchTerm && !f.finding.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !f.control_id.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const severityData = Object.entries(s.severity_distribution || {}).map(([k, v]) => ({ name: k, value: v })).filter(d => d.value > 0);
  const fwBarData = Object.entries(data?.frameworks || {}).map(([key, fw]) => ({ name: fw.label.split(" ")[0], score: fw.score, pass: fw.pass_count, fail: fw.fail_count }));

  return (
    <AdminLayout>
      <motion.div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="test-results-header">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Test Results Center</h1>
            <p className="text-sm text-slate-500 mt-1">Comprehensive view of all compliance test results, findings, and gap analysis</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1" data-testid="refresh-results-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1" data-testid="export-csv-results">
              <Download className="w-3.5 h-3.5" /> CSV
            </Button>
            <Button size="sm" onClick={handleExportPDF} disabled={pdfLoading}
              className="bg-[#FF7A59] hover:bg-[#FF8A6C] text-white gap-1" data-testid="export-pdf-scorecard">
              {pdfLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              PDF Scorecard
            </Button>
          </div>
        </div>

        {/* ── Summary Stats Row ── */}
        <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" variants={itemVariants} initial="hidden" animate="show">
          <StatCard icon={Shield} label="Total Controls" value={s.total_controls} color="#0f172a" />
          <StatCard icon={CheckCircle2} label="Pass Rate" value={`${s.overall_pass_rate}%`} sub={`${s.total_pass} passed`} color="#0E8A55" />
          <StatCard icon={XCircle} label="Open Gaps" value={s.open_findings} color="#EF4444" />
          <StatCard icon={Activity} label="In Progress" value={s.in_progress_findings} color="#F59E0B" />
          <StatCard icon={CheckCircle2} label="Resolved" value={s.resolved_findings} color="#10B981" />
          <StatCard icon={Clock} label="Avg Days Open" value={s.avg_days_open} color="#6366F1" />
        </motion.div>

        {/* ── Main Content Tabs ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="results-tabs">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="findings" className="text-xs">All Findings</TabsTrigger>
            <TabsTrigger value="frameworks" className="text-xs">Frameworks</TabsTrigger>
            <TabsTrigger value="history" className="text-xs">Run History</TabsTrigger>
          </TabsList>

          {/* ── TAB: Overview ── */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Severity Distribution Pie */}
              <Card className="bg-white border border-slate-200/80" data-testid="severity-pie-chart">
                <CardHeader className="pb-2"><CardTitle className="text-base">Severity Distribution</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={severityData} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                          paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {severityData.map((entry, idx) => (
                            <Cell key={entry.name} fill={SEVERITY_COLORS[entry.name] || PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <RTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Framework Scores Bar Chart */}
              <Card className="bg-white border border-slate-200/80" data-testid="framework-bar-chart">
                <CardHeader className="pb-2"><CardTitle className="text-base">Framework Scores</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={fwBarData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                        <RTooltip contentStyle={{ background: "#1e293b", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                        <Bar dataKey="score" fill="#FF7A59" radius={[0, 4, 4, 0]} barSize={16} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Trend */}
            <Card className="bg-white border border-slate-200/80" data-testid="score-trend-chart">
              <CardHeader className="pb-2"><CardTitle className="text-base">Compliance Score Trend (30 Days)</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data?.trend_history || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }}
                        tickFormatter={v => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })} />
                      <YAxis domain={[40, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <RTooltip contentStyle={{ background: "#1e293b", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                      <Line type="monotone" dataKey="score" stroke="#FF7A59" strokeWidth={2.5} dot={{ fill: "#FF7A59", r: 2.5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TAB: All Findings ── */}
          <TabsContent value="findings" className="space-y-4 mt-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3" data-testid="findings-filters">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search findings or control IDs..." className="pl-9 h-9 text-sm"
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="findings-search" />
              </div>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="severity-filter">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={frameworkFilter} onValueChange={setFrameworkFilter}>
                <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="framework-filter">
                  <SelectValue placeholder="Framework" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Frameworks</SelectItem>
                  {Object.keys(data?.frameworks || {}).map(fw => (
                    <SelectItem key={fw} value={fw}>{data.frameworks[fw].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-400">{filteredFindings.length} results</span>
            </div>

            {/* Findings Table */}
            <Card className="bg-white border border-slate-200/80">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="all-findings-table">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Severity</th>
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Framework</th>
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Control</th>
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Finding</th>
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Remediation</th>
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                        <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFindings.map(f => (
                        <tr key={f.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="py-2.5 px-4"><SeverityBadge severity={f.severity} /></td>
                          <td className="py-2.5 px-4 font-medium text-slate-700 text-xs">{f.framework}</td>
                          <td className="py-2.5 px-4"><span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{f.control_id}</span></td>
                          <td className="py-2.5 px-4 text-slate-600 text-xs max-w-[250px] truncate">{f.finding}</td>
                          <td className="py-2.5 px-4 text-slate-500 text-xs max-w-[250px] truncate">{f.remediation}</td>
                          <td className="py-2.5 px-4">
                            <Badge variant={f.status === "resolved" ? "default" : f.status === "in_progress" ? "secondary" : "outline"}
                              className={`text-[10px] ${f.status === "open" ? "border-red-200 text-red-600" : ""}`}>
                              {f.status === "in_progress" ? "In Progress" : f.status.charAt(0).toUpperCase() + f.status.slice(1)}
                            </Badge>
                          </td>
                          <td className="py-2.5 px-4 text-xs text-slate-500">{f.days_open}d</td>
                        </tr>
                      ))}
                      {filteredFindings.length === 0 && (
                        <tr><td colSpan={7} className="py-12 text-center text-slate-400">
                          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                          <p className="text-sm">No findings match your filters</p>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TAB: Frameworks ── */}
          <TabsContent value="frameworks" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="frameworks-grid">
              {Object.entries(data?.frameworks || {}).map(([key, fw]) => (
                <Card key={key} className="bg-white border border-slate-200/80 hover:shadow-md transition-shadow" data-testid={`fw-card-${key}`}>
                  <div className="h-1.5" style={{ backgroundColor: fw.color }} />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-sm text-slate-900">{fw.label}</h3>
                      <span className="text-2xl font-extrabold" style={{ color: fw.score >= 80 ? "#0E8A55" : fw.score >= 60 ? "#D97706" : "#C7322A" }}>
                        {fw.score}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center mb-3">
                      <div className="bg-emerald-50 rounded p-2">
                        <p className="text-lg font-bold text-emerald-700">{fw.pass_count}</p>
                        <p className="text-[10px] text-emerald-600">Pass</p>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <p className="text-lg font-bold text-red-700">{fw.fail_count}</p>
                        <p className="text-[10px] text-red-600">Fail</p>
                      </div>
                      <div className="bg-amber-50 rounded p-2">
                        <p className="text-lg font-bold text-amber-700">{fw.warn_count}</p>
                        <p className="text-[10px] text-amber-600">Warn</p>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <p className="text-lg font-bold text-blue-700">{fw.total_controls}</p>
                        <p className="text-[10px] text-blue-600">Total</p>
                      </div>
                    </div>
                    <Progress value={fw.score} className="h-1.5" />
                    <p className="text-[10px] text-slate-400 mt-2">
                      Last run: {fw.last_run_at ? new Date(fw.last_run_at).toLocaleDateString() : "Not run"}
                    </p>
                    {fw.findings && fw.findings.length > 0 && (
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Findings ({fw.findings.length})</p>
                        <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                          {fw.findings.slice(0, 5).map(f => (
                            <div key={f.id} className="flex items-center gap-2 text-xs">
                              <SeverityBadge severity={f.severity} />
                              <span className="text-slate-600 truncate flex-1">{f.finding}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ── TAB: Run History ── */}
          <TabsContent value="history" className="space-y-4 mt-4">
            <Card className="bg-white border border-slate-200/80" data-testid="run-history-table">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Completed Test Runs</CardTitle>
              </CardHeader>
              <CardContent>
                {(data?.completed_runs || []).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-semibold">Run ID</th>
                          <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-semibold">Type</th>
                          <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-semibold">Started</th>
                          <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-semibold">Completed</th>
                          <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-semibold">Score</th>
                          <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-semibold">Checks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.completed_runs.map(run => (
                          <tr key={run.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="py-2 px-3 font-mono text-xs text-slate-500">{run.id.slice(0, 8)}</td>
                            <td className="py-2 px-3"><Badge variant="outline" className="text-[10px]">{run.run_type || "MANUAL"}</Badge></td>
                            <td className="py-2 px-3 text-xs text-slate-600">{run.started_at ? new Date(run.started_at).toLocaleString() : ""}</td>
                            <td className="py-2 px-3 text-xs text-slate-600">{run.completed_at ? new Date(run.completed_at).toLocaleString() : ""}</td>
                            <td className="py-2 px-3">
                              <span className="text-lg font-bold" style={{ color: (run.overall_score || 0) >= 80 ? "#0E8A55" : "#D97706" }}>
                                {run.overall_score || "N/A"}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-xs text-slate-600">{run.progress}/{run.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-400">
                    <Activity className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">No completed test runs yet</p>
                    <p className="text-xs mt-1">Go to the Compliance Dashboard to run tests</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </AdminLayout>
  );
};

export default TestResultsCenterPage;
